import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ScanLine, Send, Loader2 } from "lucide-react";
import api, { apiError } from "../lib/api";
import { BrandMark } from "../components/Layout";
import { useAuth } from "../context/AuthContext";

const inp = "w-full bg-[#1c1c1f] border border-zinc-800 text-white rounded-xl px-3 py-2.5 focus:border-[#F26722] focus:outline-none";

export default function Scans() {
  const nav = useNavigate();
  const { user } = useAuth();
  const canCreate = user?.role !== "office";
  const [scans, setScans] = useState([]);
  const [recipients, setRecipients] = useState([]);
  const [form, setForm] = useState({ barcode: "", recipient_id: "", notes: "" });
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => { try { const { data } = await api.get("/scans"); setScans(data); } catch (e) { toast.error(apiError(e)); } }, []);
  useEffect(() => {
    load();
    api.get("/recipients").then(({ data }) => { setRecipients(data); setForm((s) => ({ ...s, recipient_id: user?.default_recipient_id || data[0]?.id || "" })); }).catch(() => {});
  }, [load, user]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.barcode.trim()) return toast.error("Barcode required");
    if (!form.recipient_id) return toast.error("Select a recipient");
    setLoading(true);
    try {
      await api.post("/scans", { barcode: form.barcode.trim(), recipient_id: form.recipient_id, notes: form.notes.trim() || undefined });
      toast.success("Scan logged");
      setForm({ ...form, barcode: "", notes: "" });
      load();
    } catch (e2) { toast.error(apiError(e2)); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] max-w-md mx-auto sm:max-w-2xl md:max-w-4xl border-x border-zinc-800/40 pb-10">
      <div className="sticky top-0 z-40 bg-[#0A0A0A]/90 backdrop-blur-md px-4 py-4 border-b border-zinc-800/70 flex items-center justify-between">
        <button onClick={() => nav("/profile")} className="flex items-center gap-2 text-zinc-300"><ArrowLeft size={20} /> Profile</button>
        <BrandMark />
      </div>
      <div className="px-4 pt-4">
        <h1 className="text-2xl font-extrabold text-white flex items-center gap-2"><ScanLine size={22} className="text-[#F26722]" /> Barcode Scans</h1>
        <p className="text-zinc-400 text-sm">Log a scanned part barcode to an office recipient.</p>

        {canCreate && (
          <form onSubmit={submit} className="bg-[#18181B] border border-zinc-800 rounded-2xl p-4 mt-4 space-y-3">
            <input data-testid="scan-barcode-input" className={inp} placeholder="Barcode / part number" value={form.barcode} onChange={(e) => setForm({ ...form, barcode: e.target.value })} />
            <select data-testid="scan-recipient-select" className={inp} value={form.recipient_id} onChange={(e) => setForm({ ...form, recipient_id: e.target.value })}>
              <option value="">Select recipient…</option>
              {recipients.map((r) => <option key={r.id} value={r.id}>{r.name} ({r.email})</option>)}
            </select>
            <input data-testid="scan-notes-input" className={inp} placeholder="Notes (optional)" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            <button data-testid="scan-submit-btn" disabled={loading} className="w-full bg-[#F26722] hover:bg-[#D95513] disabled:opacity-60 text-white font-bold py-2.5 rounded-xl flex items-center justify-center gap-2">
              {loading ? <Loader2 className="animate-spin" size={18} /> : <><Send size={16} /> Log scan</>}
            </button>
          </form>
        )}

        <div className="mt-5 space-y-2">
          {scans.length === 0 && <p className="text-zinc-500 text-center py-8" data-testid="scans-empty">No scans yet.</p>}
          {scans.map((s) => (
            <div key={s.id} data-testid={`scan-${s.id}`} className="flex items-center justify-between bg-[#18181B] border border-zinc-800 rounded-xl p-3.5">
              <div className="min-w-0">
                <p className="font-bold text-white font-mono">{s.barcode}</p>
                <p className="text-xs text-zinc-500 truncate">{s.recipient_name || s.recipient_email || ""}{s.notes ? ` · ${s.notes}` : ""}</p>
              </div>
              <span className="text-xs text-zinc-600 shrink-0 ml-2">{(s.created_at || "").slice(0, 10)}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
